import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getApplicationDetails, getApplicationByNumberAndPhone } from '../ServiceAPI/application/api';
import { FileText, CheckCircle, Clock, AlertCircle, ArrowLeft, ExternalLink } from 'lucide-react';
import { BACKEND_BASE_URL } from '../config/api';
import { isTokenUsable } from '../utils/authSession';

const getUploadUrl = (value) => {
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;

    const normalizedValue = value.replace(/\\/g, '/').trim();
    let relativePath = normalizedValue;

    if (relativePath.includes('/uploads/')) {
        relativePath = relativePath.substring(relativePath.indexOf('/uploads/') + 1);
    } else if (relativePath.startsWith('/')) {
        relativePath = relativePath.slice(1);
    }

    return `${BACKEND_BASE_URL.replace(/\/+$/, '')}/${relativePath}`;
};

const ApplicationDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { applicationId } = useParams();
    const applicationNumberFromState = location.state?.applicationNumber || null;
    const phoneFromState = location.state?.phone || null;
    const guestCredentials = applicationNumberFromState && phoneFromState
        ? { applicationNumber: applicationNumberFromState, phone: phoneFromState }
        : null;
    const [application, setApplication] = useState(null);
    const [formData, setFormData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                // Guest path: arrived here via phone + application number verification,
                // no login required - use the public lookup instead of the token-gated one.
                if (guestCredentials) {
                    const data = await getApplicationByNumberAndPhone(guestCredentials.applicationNumber, guestCredentials.phone);
                    if (data && data.application) {
                        setApplication(data.application);
                        if (data.formData) {
                            setFormData(data.formData);
                        }
                    } else {
                        setError('Application not found');
                    }
                    setLoading(false);
                    return;
                }

                const token = localStorage.getItem('token');
                const storedUser = localStorage.getItem('user');
                const hasUsableToken = isTokenUsable(token);
                let user = null;

                if (storedUser) {
                    try {
                        user = JSON.parse(storedUser);
                    } catch (parseError) {
                        console.error('Failed to parse stored user data:', parseError);
                    }
                }

                if (!hasUsableToken) {
                    if (!user?.userId) {
                        navigate('/');
                        return;
                    }
                }

                let data;

                if (hasUsableToken) {
                    try {
                        // Primary path: authenticated endpoint
                        data = await getApplicationDetails(applicationId, { silent: true });
                    } catch (primaryError) {
                        if (!user?.userId) {
                            throw primaryError;
                        }

                        // Fallback path: preserve compatibility with older cached request shapes
                        data = await getApplicationDetails(applicationId, { userId: user.userId, skipAuth: true, silent: true });
                    }
                } else {
                    data = await getApplicationDetails(applicationId, { userId: user.userId, skipAuth: true, silent: true });
                }

                if (data && data.application) {
                    setApplication(data.application);
                    if (data.formData) {
                        setFormData(data.formData);
                    }
                } else {
                    setError('Application not found');
                }
            } catch (err) {
                console.error('Error fetching application details:', err);
                setError('Failed to load application details. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (applicationId) {
            fetchDetails();
        }
    }, [applicationId, navigate, applicationNumberFromState, phoneFromState]);

    const getStatusBadge = (status) => {
        const normalizedStatus = String(status || '').toLowerCase().replace(/[_-]+/g, ' ').trim();
        switch (normalizedStatus) {
            case 'completed':
                return {
                    bgColor: 'bg-green-100 dark:bg-green-900',
                    textColor: 'text-green-800 dark:text-green-200',
                    icon: CheckCircle,
                    label: 'Completed'
                };
            case 'submitted':
                return {
                    bgColor: 'bg-yellow-100 dark:bg-yellow-900',
                    textColor: 'text-yellow-800 dark:text-yellow-200',
                    icon: Clock,
                    label: 'Submitted'
                };
            case 'documents verified':
                return {
                    bgColor: 'bg-blue-100 dark:bg-blue-900',
                    textColor: 'text-blue-800 dark:text-blue-200',
                    icon: CheckCircle,
                    label: 'Documents Verified'
                };
            case 'payment pending':
                return {
                    bgColor: 'bg-orange-100 dark:bg-orange-900',
                    textColor: 'text-orange-800 dark:text-orange-200',
                    icon: Clock,
                    label: 'Payment Pending'
                };
            case 'payment completed':
                return {
                    bgColor: 'bg-indigo-100 dark:bg-indigo-900',
                    textColor: 'text-indigo-800 dark:text-indigo-200',
                    icon: CheckCircle,
                    label: 'Payment Completed'
                };
            case 'payment didnt receive':
                return {
                    bgColor: 'bg-red-100 dark:bg-red-900',
                    textColor: 'text-red-800 dark:text-red-200',
                    icon: AlertCircle,
                    label: "Payment Didn't Receive"
                };
            case 'in processing':
                return {
                    bgColor: 'bg-purple-100 dark:bg-purple-900',
                    textColor: 'text-purple-800 dark:text-purple-200',
                    icon: Clock,
                    label: 'In Processing'
                };
            case 'rejected':
                return {
                    bgColor: 'bg-red-100 dark:bg-red-900',
                    textColor: 'text-red-800 dark:text-red-200',
                    icon: AlertCircle,
                    label: 'Rejected'
                };
            default:
                return {
                    bgColor: 'bg-gray-100 dark:bg-gray-700',
                    textColor: 'text-gray-800 dark:text-gray-200',
                    icon: FileText,
                    label: normalizedStatus
                        ? normalizedStatus.replace(/\b\w/g, (c) => c.toUpperCase())
                        : 'Unknown'
                };
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-gray-300 dark:border-gray-600 border-t-cyan-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading details...</p>
                </div>
            </div>
        );
    }

    if (error || !application) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
                <div className="max-w-4xl mx-auto">
                    <button onClick={() => navigate('/my-applications')} className="flex items-center gap-2 mb-6 text-cyan-600 hover:text-cyan-700 font-medium">
                        <ArrowLeft className="w-4 h-4" /> Back to My Applications
                    </button>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                        <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                        <h2 className="text-2xl font-bold mb-2">Error</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">{error || 'Something went wrong'}</p>
                        <button onClick={() => navigate('/my-applications')} className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
                            Return to Applications
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const statusInfo = getStatusBadge(application.Application_status || application.Status);
    const StatusIcon = statusInfo.icon;

    return (
        <div className="bg-gray-50 dark:bg-gray-900 min-h-screen p-4 md:p-8">
            <div className="max-w-4xl mx-auto">
                <button onClick={() => navigate('/my-applications')} className="flex items-center gap-2 mb-6 text-cyan-600 hover:text-cyan-700 font-medium transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back to My Applications
                </button>

                <div className="grid grid-cols-1 gap-6">
                    {/* Header Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex flex-wrap justify-between items-center gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Application Details</h1>
                                <p className="text-sm text-gray-500 mt-1">ID: <span className="font-mono text-cyan-600">{applicationId}</span></p>
                            </div>
                            <div className={`${statusInfo.bgColor} ${statusInfo.textColor} px-4 py-2 rounded-full flex items-center gap-2 text-sm font-semibold`}>
                                <StatusIcon className="w-4 h-4" />
                                {statusInfo.label}
                            </div>
                        </div>

                        <div className="p-6">
                            <h3 className="text-lg font-semibold border-b pb-2 dark:border-gray-700 mb-4">Service Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                                    <span className="text-gray-500 text-sm">Service Name</span>
                                    <span className="font-bold text-gray-900 dark:text-white">{application.ServiceName}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                                    <span className="text-gray-500 text-sm">Service Type</span>
                                    <span className="font-bold text-gray-900 dark:text-white">{application.ServiceType || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                                    <span className="text-gray-500 text-sm">Service Category</span>
                                    <span className="font-bold text-gray-900 dark:text-white">{application.ServiceCategory || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                                    <span className="text-gray-500 text-sm">Submitted On</span>
                                    <span className="font-bold text-gray-900 dark:text-white">
                                        {new Date(application.Created_at || application.ApplicationDate).toLocaleDateString('en-IN', {
                                            day: 'numeric', month: 'long', year: 'numeric'
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Data Card */}
                    {formData && formData.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                                <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                                    <FileText className="w-5 h-5 text-cyan-600" />
                                    Submitted Application Data
                                </h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300">
                                        <tr>
                                            <th className="px-6 py-4 font-semibold text-sm uppercase tracking-wider">Field Name</th>
                                            <th className="px-6 py-4 font-semibold text-sm uppercase tracking-wider">Submitted Response</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {formData.map((field, index) => {
                                            const isFile = field.InputType === 'file' || field.Input_type === 'file' || (typeof field.Value === 'string' && field.Value.startsWith('uploads/'));
                                            const isReuploadRequested = field.ReuploadRequested === 1;

                                            return (
                                                <tr key={index} className={`hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors ${isReuploadRequested ? 'bg-orange-50 dark:bg-orange-900/10' : ''}`}>
                                                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            {field.FieldLabel || field.Field_label}
                                                            {isReuploadRequested && (
                                                                <span className="flex h-2 w-2 rounded-full bg-orange-500 animate-pulse"></span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex flex-wrap items-center gap-3">
                                                            {isFile ? (
                                                                <a
                                                                    href={getUploadUrl(field.Value)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 text-sm font-bold border border-cyan-100 dark:border-cyan-800 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 transition-all shadow-sm group"
                                                                >
                                                                    <FileText className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                                                    View Document
                                                                    <ExternalLink className="w-3 h-3 ml-1" />
                                                                </a>
                                                            ) : (
                                                                <span className="font-bold text-gray-900 dark:text-white">
                                                                    {field.Value || field.field_value || 'N/A'}
                                                                </span>
                                                            )}

                                                            {isReuploadRequested && (
                                                                <button
                                                                    onClick={() => navigate(`/application-form/${application.Service_id}?applicationId=${applicationId}&dataId=${field.DataId}`)}
                                                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-all shadow-md transform hover:scale-105"
                                                                >
                                                                    <ExternalLink className="w-4 h-4" />
                                                                    Reupload Now
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Remarks Section */}
                    {application.Remarks && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded-lg p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-yellow-800 dark:text-yellow-200 flex items-center gap-2 mb-2">
                                <AlertCircle className="w-5 h-5" />
                                Admin Feedback
                            </h3>
                            <p className="text-yellow-700 dark:text-yellow-300 leading-relaxed font-medium">
                                {application.Remarks}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApplicationDetails;